// tabs/player-settings.ts — client-side port of the PC sheet's Player Settings tab
//   options/game/character_sheet_display/player_settings/_player_settings_inc.php
//
// A permission-gated action bar (share / copy / edit / uploads / force-refresh /
// active + allow-copying toggles) plus the player attribution block. All actions
// call the SAME existing template.js globals (copyToClipboard, queryData, openSideLog,
// forceCasterArrReset, hideEl/showEl) so behavior is unchanged — only the render moves
// to TS. Modernized into a single tidy toolbar card.
//
// NOTE on the copy button: PHP gates it on has_copy_permission() = admin || owner ||
// allow_copying==1, shown to non-owners only. Client-side we resolve the non-owner +
// allow_copying==1 case (the common one); the admin-copying-a-non-copyable edge is not
// reproduced (admins rarely hit it and have other paths). Owner gets Edit instead.

import type { CachedCaster } from '../types';
import { num, pcField, sanitizeDomId } from '../read-helpers';
import { trainingGroundsGate, type TrainingGateFields } from '../../training-grounds/gate';
import { ftCurrentTruthSink } from '../../shared/truth-sink';

const ROOT = () => BASE_URL;
const trainingGroundsReads = new Map<string, { at: number; read: Promise<{ fields: TrainingGateFields; receivedAt: number } | null> }>();
// A Settings repaint inside this window reuses the read; after it, combat / session changes are read afresh.
const TRAINING_GROUNDS_READ_TTL_MS = 30_000;

/** One lazy read per PC per repaint window; a failed read leaves navigation available. */
function refreshTrainingGroundsLink(pcId: string): void {
    const held = trainingGroundsReads.get(pcId);
    let read = held && Date.now() - held.at < TRAINING_GROUNDS_READ_TTL_MS ? held.read : undefined;
    if (!read) {
        read = fetch(`${ROOT()}options/api/training_grounds_base_inc.php?player_character_id=${encodeURIComponent(pcId)}`, {
            credentials: 'same-origin', cache: 'no-store',
        }).then(async response => {
            if (!response.ok) return null;
            const data = await response.json() as { ok?: boolean; gate?: TrainingGateFields };
            return data.ok && data.gate && Array.isArray(data.gate.sessions)
                ? { fields: data.gate, receivedAt: Date.now() } : null;
        }).catch(() => null);
        trainingGroundsReads.set(pcId, { at: Date.now(), read });
    }
    void read.then(result => {
        if (!result) return;
        // The renderer returns HTML to its caller. Wait until that HTML has mounted.
        requestAnimationFrame(() => {
            // A slower, older read never overwrites the state a newer one already applied.
            if (trainingGroundsReads.get(pcId)?.read !== read) return;
            const gate = trainingGroundsGate({ inCombat: result.fields.in_combat, sessions: result.fields.sessions,
                now: Date.parse(result.fields.server_now) + Date.now() - result.receivedAt });
            for (const link of document.querySelectorAll<HTMLAnchorElement>('a[data-ft-training-grounds]')) {
                if (link.dataset.ftTrainingGrounds !== pcId) continue;
                link.classList.toggle('disabled', !gate.enabled);
                link.setAttribute('aria-disabled', String(!gate.enabled));
                // A disabled link drops its href, so a middle-click or the context menu cannot open it either.
                if (!gate.enabled && link.hasAttribute('href')) {
                    link.dataset.ftTrainingGroundsHref = link.getAttribute('href') ?? ''; link.removeAttribute('href');
                } else if (gate.enabled && link.dataset.ftTrainingGroundsHref) {
                    link.setAttribute('href', link.dataset.ftTrainingGroundsHref);
                }
                const title = gate.reason === 'in_combat' ? 'In combat'
                    : gate.reason === 'session_live' ? 'A session is in progress' : 'Training Grounds';
                link.title = title;
                // iconMap owns the Bootstrap tooltip on the icon inside the anchor.
                for (const icon of link.querySelectorAll<HTMLElement>('[data-bs-toggle="tooltip"]')) {
                    icon.setAttribute('data-bs-title', title); icon.setAttribute('data-bs-original-title', title);
                    icon.title = title;
                }
            }
        });
    });
}

document.addEventListener('click', event => {
    if (event.target instanceof Element && event.target.closest('a[data-ft-training-grounds][aria-disabled="true"]')) {
        event.preventDefault(); event.stopPropagation();
    }
});

/** A plain action button calling an inline onclick (icon-only, dark by default). */
function actionBtn(opts: {
    icon: string; onclick: string; id?: string; cls?: string; title?: string; styleHidden?: boolean;
}): string {
    const id = opts.id ? ` id="${escapeHtml(opts.id)}"` : '';
    const style = opts.styleHidden ? ` style="display:none;"` : '';
    const label = opts.title ? ` aria-label="${escapeHtml(opts.title)}"` : '';
    return `<button type="button"${id} class="btn btn-lg ${opts.cls ?? 'btn-dark'} border"${label}${style} onclick="${opts.onclick}">${iconMap(opts.icon, 25, 25, opts.title || '')}</button>`;
}

/** An anchor styled as a button (Edit / Upload links). */
function actionLink(href: string, icon: string, title: string, attrs = ''): string {
    return `<a href="${href}" role="button" class="btn btn-lg btn-dark border" aria-label="${escapeHtml(title)}"${attrs}>${iconMap(icon, 25, 25, title)}</a>`;
}

export function buildPlayerSettingsHtml(caster: CachedCaster, casterId: string): string {
    const sid = sanitizeDomId(casterId);
    const cid = escapeHtml(casterId);
    const isOwner = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const userId = window.FateTide?.user?.id || '';
    const allowCopying = num(pcField(caster, 'player_character_allow_copying'));
    const inactive = num(pcField(caster, 'player_character_inactive'));

    const buttons: string[] = [];

    // Share — attached characters enter FT3D; unattached characters stay on their sheet.
    const shareUrl = `${ROOT()}item.php?id=${cid}`;
    buttons.push(
        actionBtn({
            icon: 'share_player_character',
            id: `btn_share_pc_sheet_${sid}`,
            title: 'Copy share link',
            onclick: `copyToClipboard('#hidden_item_id_share_${sid}'); showCopiedTooltip('btn_share_pc_sheet_${sid}');`,
        }) +
        `<p id="hidden_item_id_share_${sid}" class="d-none">${shareUrl}</p>`,
    );

    // Explicit sheet share. This uses the normal character sheet and never mounts a map runtime.
    const sheetOnlyUrl = `${shareUrl}&amp;view=sheet`;
    buttons.push(
        actionBtn({
            icon: 'character_description',
            id: `btn_share_pc_sheet_only_${sid}`,
            title: 'Copy sheet-only link',
            onclick: `copyToClipboard('#hidden_item_id_sheet_share_${sid}'); showCopiedTooltip('btn_share_pc_sheet_only_${sid}');`,
        }) +
        `<p id="hidden_item_id_sheet_share_${sid}" class="d-none">${sheetOnlyUrl}</p>`,
    );

    // Copy to my account — non-owner + logged-in + copyable.
    if (userId !== '' && !isOwner && allowCopying === 1) {
        buttons.push(actionBtn({
            icon: 'copy_player_character',
            title: 'Copy to my account',
            onclick: `copyPlayerCharacter('${cid}');`,
        }));
    }

    if (isOwner) {
        // A local room already owns this synthetic actor; it has no persisted entry gate to re-read.
        if (!ftCurrentTruthSink()?.heldBase) {
            buttons.push(actionLink(`${ROOT()}training_grounds.php?player_character_id=${encodeURIComponent(casterId)}`,
                'training_grounds', 'Training Grounds', ` data-ft-training-grounds="${cid}" title="Training Grounds"`));
            refreshTrainingGroundsLink(casterId);
        }
        buttons.push(actionLink(`${ROOT()}tools/player_characters/create_player_character.php?id=${cid}`, 'edit_player_character', 'Edit character'));
        buttons.push(actionLink(`${ROOT()}tools/player_characters/upload/upload_player_character_picture.php?id=${cid}`, 'upload_player_character_picture', 'Upload picture'));
        buttons.push(actionBtn({
            icon: 'force_refresh_caster', id: `btn_pc_force_refresh_caster_${sid}`, title: 'Force refresh',
            onclick: 'forceCasterArrReset();',
        }));

        // Active / inactive toggle pair (state-driven initial visibility).
        // The inactive direction confirms (2026-09-02: players hunting for "make me active" clicked it).
        const setInactive = `if(!window.confirm('Set this character INACTIVE? An inactive character leaves the active party and is skipped by session and party moves. (To rejoin later, use Mark active.)'))return; openSideLog(); queryData('${ROOT()}options/tools/player_characters/update/update_player_character_inactive_inc.php','div_sidelog_warning',{ item_id: '${cid}', player_character_inactive: 1 }); hideEl('btn_toggle_character_active_${sid}'); showEl('btn_toggle_character_inactive_${sid}');`;
        const setActive = `openSideLog(); queryData('${ROOT()}options/tools/player_characters/update/update_player_character_inactive_inc.php','div_sidelog_warning',{ item_id: '${cid}', player_character_inactive: 0 }); showEl('btn_toggle_character_active_${sid}'); hideEl('btn_toggle_character_inactive_${sid}');`;
        buttons.push(
            actionBtn({ icon: 'player_character_active', id: `btn_toggle_character_active_${sid}`, cls: 'btn-primary', title: 'Character is ACTIVE — click to set inactive', onclick: setInactive, styleHidden: inactive === 1 }) +
            actionBtn({ icon: 'player_character_inactive', id: `btn_toggle_character_inactive_${sid}`, cls: 'btn-danger', title: 'Mark active', onclick: setActive, styleHidden: inactive === 0 }),
        );

        // Allow / disallow copying toggle pair.
        const setAllow = `openSideLog(); queryData('${ROOT()}options/tools/player_characters/update/update_player_character_allow_copying_inc.php','div_sidelog_warning',{ item_id: '${cid}', player_character_allow_copying: 1 }); hideEl('btn_toggle_pc_allow_copy_${sid}'); showEl('btn_toggle_pc_disallow_copy_${sid}');`;
        const setDisallow = `openSideLog(); queryData('${ROOT()}options/tools/player_characters/update/update_player_character_allow_copying_inc.php','div_sidelog_warning',{ item_id: '${cid}', player_character_allow_copying: 0 }); showEl('btn_toggle_pc_allow_copy_${sid}'); hideEl('btn_toggle_pc_disallow_copy_${sid}');`;
        buttons.push(
            actionBtn({ icon: 'share_player_character_disallowed', id: `btn_toggle_pc_allow_copy_${sid}`, title: 'Allow copying', onclick: setAllow, styleHidden: allowCopying === 1 }) +
            actionBtn({ icon: 'share_player_character_allowed', id: `btn_toggle_pc_disallow_copy_${sid}`, cls: 'btn-light', title: 'Disallow copying', onclick: setDisallow, styleHidden: allowCopying === 0 }),
        );
    }

    // ── attribution block ──
    const username = pcField(caster, 'player_username');
    const createdById = pcField(caster, 'player_character_created_by_id') ?? '';
    let attribution = `<div class="ft-pc-settings__attr">
        <div><span class="fw-bold">Player Username:</span> <a class="link-primary" href="${ROOT()}user.php?user_id=${escapeHtml(String(createdById))}">${escapeHtml(String(username ?? ''))}</a></div>`;
    const origId = pcField(caster, 'player_originally_created_by_user_id');
    if (origId !== undefined && origId !== null && origId !== '') {
        const origName = pcField(caster, 'player_originally_username');
        const origLabel = origName !== undefined && origName !== null && origName !== '' ? String(origName) : String(origId);
        attribution += `<div><span class="fw-bold">Originally Created By:</span> <a class="link-secondary" href="${ROOT()}user.php?user_id=${escapeHtml(String(origId))}">${escapeHtml(origLabel)}</a></div>`;
    }
    attribution += `</div>`;

    return `<div class="ft-pc-settings">
        <div class="ft-pc-settings__bar">${buttons.join('')}</div>
        ${attribution}
    </div>`;
}
